import { ILeagueConfig } from 'fitbeat.models/dist/leaderboard/leaderboard-league-config';
import { ITeamConfig } from 'fitbeat.models/dist/leaderboard/leaderboard-team-config';
import React, { useEffect, useState } from 'react';

interface IConfigListEditorProps {
  title: string;
  teams: ITeamConfig[];
  leagues: ILeagueConfig[];
  enableLeagues?: boolean;
  selectedTeamIndex?: number | null;
  onAddTeam: (team: ITeamConfig) => void;
  onUpdateTeam: (index: number, team: ITeamConfig) => void;
  onClearSelection?: () => void;
}

const emptyTeam: ITeamConfig = { name: '' };

const TeamListEditor: React.FC<IConfigListEditorProps> = ({
  title,
  teams,
  leagues,
  enableLeagues = false,
  selectedTeamIndex = null,
  onAddTeam,
  onUpdateTeam,
  onClearSelection,
}) => {
  const isEditMode = selectedTeamIndex !== null && selectedTeamIndex >= 0 && selectedTeamIndex < teams.length;

  const [newTeam, setNewTeam] = useState<ITeamConfig>(emptyTeam);

  useEffect(() => {
    if (isEditMode) {
      setNewTeam(teams[selectedTeamIndex]);
    } else {
      setNewTeam(emptyTeam);
    }
  }, [selectedTeamIndex]);

  const handleSubmit = () => {
    if (isEditMode) {
      onUpdateTeam(selectedTeamIndex, newTeam);
      onClearSelection?.();
    } else {
      onAddTeam(newTeam);
      setNewTeam(emptyTeam);
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isEditMode && onClearSelection) {
      onClearSelection();
    } else {
      setNewTeam(emptyTeam);
    }
    e.currentTarget.blur();
  };

  return (
    <div style={styles.formColumn}>
      <div style={styles.scrollableContent}>
        <h3 style={styles.columnHeader}>
          {title}
        </h3>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Team Display Name</label>
          <input
            type='text'
            value={newTeam.name}
            onChange={(e) =>
              setNewTeam({
                ...newTeam,
                name: e.target.value,
              })}
            placeholder='Team Display Name'
            style={styles.inputField}
            disabled={isEditMode}
          />
        </div>
        {enableLeagues && (
          <div style={styles.inputGroup}>
            <label style={styles.label}>League</label>
            <select
              value={newTeam.league ?? ''}
              onChange={(e) =>
                setNewTeam({
                  ...newTeam,
                  league: e.target.value === '' ? undefined : e.target.value,
                })}
              style={styles.inputField}
            >
              <option value=''>Select League (optional)</option>
              {leagues.map((league) => (
                <option key={league.id} value={league.id}>
                  {league.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Social link</label>
          <input
            type='text'
            value={newTeam.groupLink ?? ''}
            onChange={(e) =>
              setNewTeam({ ...newTeam, groupLink: e.target.value })}
            placeholder='Social link (optional)'
            style={styles.inputField}
          />
        </div>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Target size</label>
          <input
            type='number'
            min={1}
            step={1}
            value={newTeam.size !== undefined ? newTeam.size : ''}
            onChange={(e) =>
              setNewTeam({
                ...newTeam,
                size: e.target.value === '' ? undefined : Number(e.target.value),
              })}
            placeholder='Target size (optional)'
            style={styles.inputField}
          />
          <p style={styles.helpText}>
            Assign a target size for the daily team participation bonus. Leave blank to exclude this team.
          </p>
        </div>
      </div>
      <div style={styles.buttonRow}>
        <p style={styles.helpText}>
          {isEditMode
            ? 'Apply to the table.'
            : 'Add the team to the table.'}
        </p>
        <button type='button' onClick={handleSubmit} className='jr-btn-default'>
          {isEditMode ? 'Apply to table' : 'Add'}
        </button>
        <button type='button' onClick={handleCancel} className='jr-btn-default-outlined'>
          {isEditMode ? 'Cancel' : 'Clear'}
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    marginRight: '16px',
    padding: '16px',
  },
  scrollableContent: {
    overflow: 'visible',
  },
  columnHeader: {
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  teamRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    borderBottom: '1px solid #ccc',
    paddingBottom: '8px',
  },
  teamFields: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '12px',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: '4px',
    color: '#333',
  },
  helpText: {
    fontSize: '0.8rem',
    color: '#555',
    marginTop: '-4px',
    marginBottom: '4px',
  },
  inputField: {
    width: '100%',
    padding: '8px',
    marginBottom: '8px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  leagueSelect: {
    width: '100%',
    padding: '4px',
    marginTop: '4px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '16px',
  },
};

export default TeamListEditor;
